import { startRegistration, startAuthentication } from '@simplewebauthn/browser'
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser'
import { api } from '@/lib/api'

const API_BASE = import.meta.env.VITE_API_URL ?? '/api'

export interface PasskeySummary {
  id: string
  deviceName: string | null
  createdAt: string
  lastUsedAt: string | null
}

export interface PasskeyLoginResult {
  token: string
  accessToken: string
  refreshToken?: string
  user: { id: string; email: string; name: string | null; role: string; [k: string]: unknown }
}

// The authed fetcher to run a ceremony through. Defaults to the contractor `api`
// (cookie session); the admin portal passes its own `adminApi` (localStorage
// token), so contractor + admin share one ceremony path against the same
// role-agnostic endpoints.
export type PasskeyFetcher = <T>(path: string, options?: RequestInit) => Promise<T>

export const listPasskeys = (fetcher: PasskeyFetcher = api) =>
  fetcher<PasskeySummary[]>('/auth/passkeys')

export const deletePasskey = (id: string, fetcher: PasskeyFetcher = api) =>
  fetcher<{ success: true }>(`/auth/passkeys/${id}`, { method: 'DELETE' })

/**
 * Registration ceremony for an authenticated account (JWT auto-attached by
 * api()): fetch options → run the browser WebAuthn prompt → POST the attestation.
 */
export async function registerPasskey(
  deviceName?: string,
  fetcher: PasskeyFetcher = api,
): Promise<void> {
  const optionsJSON = await fetcher<PublicKeyCredentialCreationOptionsJSON>(
    '/auth/passkeys/register/begin',
    { method: 'POST' },
  )
  const response = await startRegistration({ optionsJSON })
  const name = deviceName || defaultDeviceName()
  await fetcher('/auth/passkeys/register/finish', {
    method: 'POST',
    body: JSON.stringify({ response, ...(name ? { deviceName: name } : {}) }),
  })
}

// A friendly default label so multiple passkeys are distinguishable in the
// list (e.g. "Chrome on Mac"). Coarse UA sniffing via includes() — not message
// parsing, and only cosmetic.
function defaultDeviceName(): string {
  if (typeof navigator === 'undefined') return 'Passkey'
  const ua = navigator.userAgent
  const has = (s: string) => ua.includes(s)
  const platform = has('iPhone') || has('iPad') ? 'iPhone/iPad'
    : has('Android') ? 'Android'
    : has('Macintosh') ? 'Mac'
    : has('Windows') ? 'Windows'
    : has('Linux') ? 'Linux'
    : 'this device'
  // Order matters: Edge & Chrome UAs both contain "Chrome/"; Chrome contains "Safari/".
  const browser = has('Edg/') ? 'Edge'
    : has('Chrome/') ? 'Chrome'
    : has('Firefox/') ? 'Firefox'
    : has('Safari/') ? 'Safari'
    : 'Browser'
  return `${browser} on ${platform}`
}

/**
 * Authentication ceremony (pre-login, so plain fetch — no token to attach and we
 * don't want the authed fetcher's refresh logic). Returns a session on success.
 */
export async function passkeyLogin(email?: string): Promise<PasskeyLoginResult> {
  const beginRes = await fetch(`${API_BASE}/auth/passkeys/login/begin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(email ? { email } : {}),
  })
  if (!beginRes.ok) throw new Error('Could not start passkey sign-in')
  const optionsJSON: PublicKeyCredentialRequestOptionsJSON = await beginRes.json()

  const response = await startAuthentication({ optionsJSON })

  const finishRes = await fetch(`${API_BASE}/auth/passkeys/login/finish`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // store the httpOnly auth cookies the server sets on a successful passkey login.
    credentials: 'include',
    body: JSON.stringify({ response }),
  })
  if (!finishRes.ok) {
    const err = await finishRes.json().catch(() => ({}))
    throw new Error(err.message ?? 'Passkey sign-in failed')
  }
  return finishRes.json()
}
